import * as React from 'react';
import { defaultFilter } from 'cmdk';
import { ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export type Option = {
  value: number | string;
  label: string;
};

export interface MultiSelectProps {
  options: Option[];
  value: Option[] | null;
  onChange: (selected: Option[]) => void;
  name?: string;
  placeholder?: string;
  /**
   * 'chips' shows selected items as removable badges in the trigger (default).
   * 'count' shows the placeholder + a count badge with the first selected
   * label inline — useful for compact filter bars.
   */
  displayMode?: 'chips' | 'count';
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /**
   * While true, the menu shows a spinner and `loadingMessage` instead of the
   * empty message — for options sourced from an in-flight async request.
   */
  loading?: boolean;
  loadingMessage?: string;
  /** Show a "Select all" toggle at the top of the menu. */
  isSelectAll?: boolean;
  selectAllLabel?: string;
  disabled?: boolean;
  errors?: string | boolean | null;
  className?: string;
  triggerClassName?: string;
  'aria-label'?: string;
}

const optionKey = (o: Option): string => String(o.value);

const isOptionSelected = (selected: Option[], opt: Option): boolean =>
  selected.some((s) => optionKey(s) === optionKey(opt));

const Chip = ({
  option,
  onRemove,
}: {
  option: Option;
  onRemove: (e: React.MouseEvent | React.PointerEvent) => void;
}) => (
  <span className="inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-xs font-medium text-accent-foreground">
    {option.label}
    <button
      type="button"
      aria-label={`Remove ${option.label}`}
      onPointerDown={onRemove}
      onClick={onRemove}
      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm hover:bg-destructive/20"
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

/**
 * Multi-choice dropdown with checkbox options and removable selection badges.
 * Set `loading` to swap the option list for a spinner while options are being
 * fetched (`loadingMessage` overrides the default text).
 *
 * @category Forms
 */
const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  function MultiSelect(
    {
      options,
      value,
      onChange,
      name,
      placeholder,
      displayMode = 'chips',
      searchable = true,
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results.',
      loading = false,
      loadingMessage = 'Loading…',
      isSelectAll = false,
      selectAllLabel,
      disabled,
      errors,
      className,
      triggerClassName,
      'aria-label': ariaLabel,
    },
    ref
  ) {
    const [open, setOpen] = React.useState(false);

    // Type-to-filter must score the LABEL only. Each CommandItem's `value` is
    // its option key — required for identity, because option labels are not
    // unique (the services facet ships several genuinely duplicate names) —
    // and cmdk's default filter scores that value string. With an opaque key
    // in it, a search matched options through their own identifiers: typing
    // 'e29b' hit an account whose entityUUID merely contained those hex
    // characters, burying the real match among fuzzy nonsense. Mapping the
    // key back to its label here keeps identity and matching separate.
    // Keyed on the TRIMMED key: cmdk trims item values on registration and
    // hands that trimmed string to the filter, so a padded key (raw backend
    // names in the Ordered By facet) would otherwise never look up.
    const labelByValue = React.useMemo(
      () => new Map(options.map((o) => [optionKey(o).trim(), o.label])),
      [options]
    );
    const filterByLabel = React.useCallback(
      (value: string, search: string) => {
        const label = labelByValue.get(value);
        // Non-option rows (the "Select all" toggle) have no label to match;
        // they drop out of an active search rather than scoring on their
        // sentinel value.
        if (label === undefined) return 0;
        return defaultFilter(label, search);
      },
      [labelByValue]
    );

    // When this menu opens inside a Radix Dialog/Sheet, the popover is
    // portaled to <body>, outside the dialog's react-remove-scroll subtree.
    // That library registers a bubble-phase `wheel` listener on `document` and
    // calls preventDefault() on wheel events it doesn't recognize as belonging
    // to the lock — which silently kills wheel scrolling inside this portaled
    // list (keyboard nav and type-to-filter still work, so the menu looks
    // "stuck"). Stopping the wheel event before it reaches `document` lets the
    // browser scroll the list natively while keeping the lock from cancelling
    // it. A native listener is used because React's synthetic events don't
    // reliably fire for body-portaled nodes.
    const listWheelCleanup = React.useRef<(() => void) | null>(null);
    const listNode = React.useRef<HTMLDivElement | null>(null);
    const listRef = React.useCallback((node: HTMLDivElement | null) => {
      listWheelCleanup.current?.();
      listWheelCleanup.current = null;
      listNode.current = node;
      if (node) {
        const stopWheelPropagation = (e: WheelEvent) => e.stopPropagation();
        node.addEventListener('wheel', stopWheelPropagation);
        listWheelCleanup.current = () =>
          node.removeEventListener('wheel', stopWheelPropagation);
      }
    }, []);

    // Filtering must return the list to the top. cmdk removes non-matching
    // options from the DOM, and the browser preserves scrollTop while the
    // content shrinks beneath it — so a list scrolled part-way down (which is
    // exactly what someone does while browsing 333 accounts before deciding
    // to type) clamps to the BOTTOM of each smaller result set. The observed
    // signature was scrollHeight - scrollTop pinned at the list's own height
    // through every keystroke, stranding the best match above the fold.
    // Deferred a frame so it lands after cmdk has re-rendered and run its own
    // scroll-into-view for the newly auto-selected first item.
    const resetListScroll = React.useCallback(() => {
      requestAnimationFrame(() => {
        if (listNode.current) listNode.current.scrollTop = 0;
      });
    }, []);

    const selected = value ?? [];
    const hasError = Boolean(errors);
    const allSelected =
      options.length > 0 && selected.length === options.length;

    const toggleOption = (opt: Option) => {
      if (isOptionSelected(selected, opt)) {
        onChange(selected.filter((s) => optionKey(s) !== optionKey(opt)));
      } else {
        onChange([...selected, opt]);
      }
    };

    const toggleSelectAll = () => {
      onChange(allSelected ? [] : [...options]);
    };

    const removeOne = (opt: Option) => (
      e: React.MouseEvent | React.PointerEvent
    ) => {
      e.preventDefault();
      e.stopPropagation();
      onChange(selected.filter((s) => optionKey(s) !== optionKey(opt)));
    };

    const renderTrigger = () => {
      if (displayMode === 'count') {
        const count = selected.length;
        return (
          <span className="flex flex-1 items-center gap-1.5 truncate">
            <span
              className={cn(
                'flex-1 truncate text-left text-sm',
                count === 0 && 'text-muted-foreground'
              )}
            >
              {count === 0
                ? placeholder
                : `${selected[0].label}${count > 1 ? '...' : ''}`}
            </span>
            {count > 0 && (
              <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold leading-none text-primary-foreground">
                {count}
              </span>
            )}
          </span>
        );
      }

      // 'chips' mode
      if (selected.length === 0) {
        return (
          <span className="flex-1 truncate text-left text-sm text-muted-foreground">
            {placeholder}
          </span>
        );
      }
      return (
        <span className="flex flex-1 flex-wrap items-center gap-1">
          {selected.map((s) => (
            <Chip key={optionKey(s)} option={s} onRemove={removeOne(s)} />
          ))}
        </span>
      );
    };

    return (
      <div className={cn('w-full', className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={ariaLabel}
              aria-invalid={hasError || undefined}
              disabled={disabled}
              name={name}
              className={cn(
                'w-full justify-between bg-background font-normal',
                'min-h-10 max-lg:min-h-11 h-auto py-1.5',
                hasError && 'border-destructive',
                triggerClassName
              )}
            >
              {renderTrigger()}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="min-w-[var(--radix-popover-trigger-width)] w-auto p-0"
          >
            <Command shouldFilter={searchable} filter={filterByLabel}>
              {searchable && (
                <CommandInput
                  placeholder={searchPlaceholder}
                  onValueChange={resetListScroll}
                />
              )}
              <CommandList ref={listRef}>
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                    <Spinner size="sm" />
                    <span>{loadingMessage}</span>
                  </div>
                ) : (
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                )}
                {!loading && isSelectAll && options.length > 0 && (
                  <>
                    <CommandGroup>
                      <CommandItem
                        value="__select_all__"
                        onSelect={toggleSelectAll}
                      >
                        <Checkbox
                          checked={allSelected}
                          indeterminate={
                            !allSelected && selected.length > 0
                          }
                        />
                        <span className="ml-2">
                          {selectAllLabel ??
                            (allSelected
                              ? `Selected all (${options.length})`
                              : `Select all (${options.length})`)}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}
                <CommandGroup>
                  {options.map((o) => {
                    const sel = isOptionSelected(selected, o);
                    return (
                      <CommandItem
                        key={optionKey(o)}
                        value={optionKey(o)}
                        onSelect={() => toggleOption(o)}
                      >
                        <Checkbox checked={sel} />
                        <span className="ml-2 truncate">{o.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

/**
 * Tiny visual checkbox (no Radix dependency) that supports an
 * indeterminate state — the cmdk CommandItem already handles its own
 * keyboard/focus/selected styling, so we only need a presentational
 * checkbox glyph here.
 */
const Checkbox = ({
  checked,
  indeterminate,
}: {
  checked: boolean;
  indeterminate?: boolean;
}) => (
  <span
    aria-hidden="true"
    className={cn(
      'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
      checked || indeterminate
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-input'
    )}
  >
    {indeterminate ? (
      <span className="block h-0.5 w-2 bg-current" />
    ) : checked ? (
      <svg
        viewBox="0 0 16 16"
        className="h-3 w-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="3 8 7 12 13 4" />
      </svg>
    ) : null}
  </span>
);

export { MultiSelect };
